import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { feedbackMessagesDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { feedbackMessageInputSchema } from '../../lib/schemas/feedbackMessage.js';
import type { FeedbackMessageRole } from '../../types/feedbackMessage.js';
import { timeAgo } from '../../lib/utils/time.js';
import { toAppError } from '../../lib/utils/error.js';
import { logger } from '../../lib/utils/log.js';
import { Spinner } from '../shared/Spinner.js';

interface Props {
  feedbackId: string;
  /** 親 feedback.createdBy（メッセージの feedbackOwnerUid に保存し Rules で検証される） */
  feedbackOwnerUid: string;
  /** 閲覧者の立場。投稿者なら 'user'、管理者なら 'admin' */
  viewerRole: FeedbackMessageRole;
  /** 送信成功時。親が属する一覧 query を invalidate する用途 */
  onSent?: () => void;
}

/**
 * フィードバック1件のスレッド表示＋返信フォーム。
 * 投稿者画面（MyFeedbackList）と管理者画面（FeedbackTab）の両方から使う共有部品。
 * メッセージ送信時のロールは viewerRole から渡すため Rules の authorRole 制約と一致する。
 */
export function FeedbackThread({
  feedbackId,
  feedbackOwnerUid,
  viewerRole,
  onSent,
}: Props) {
  const { profile } = useAuth();
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const messagesKey = ['feedback', feedbackId, 'messages'];

  const msgsQ = useQuery({
    queryKey: messagesKey,
    queryFn: () => feedbackMessagesDb.list(feedbackId),
  });

  const send = useMutation({
    mutationFn: async () => {
      if (!profile) throw new Error('not signed in');
      const parsed = feedbackMessageInputSchema.parse({ body });
      await feedbackMessagesDb.create(feedbackId, {
        feedbackOwnerUid,
        authorUid: profile.id,
        authorRole: viewerRole,
        body: parsed.body,
      });
    },
    onSuccess: async () => {
      setBody('');
      setError(null);
      await qc.invalidateQueries({ queryKey: messagesKey });
      onSent?.();
    },
    onError: (err) => {
      logger.error('feedback message create failed', { err: String(err) });
      setError(toAppError(err).userMessage);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim() || send.isPending) return;
    send.mutate();
  }

  const messages = msgsQ.data ?? [];

  return (
    <div className="fb-thread">
      <div className="fb-thread-msgs">
        {msgsQ.isPending ? (
          <Spinner />
        ) : messages.length === 0 ? (
          <p className="fb-thread-empty">まだ返信はありません。</p>
        ) : (
          messages.map((m) => {
            const mine = m.authorRole === viewerRole;
            return (
              <div
                key={m.id}
                className={`fb-msg ${mine ? 'fb-msg-mine' : 'fb-msg-other'}`}
              >
                <div className="fb-msg-bubble">{m.body}</div>
                <div className="fb-msg-meta">
                  <span>{m.authorRole === 'admin' ? '運営' : '投稿者'}</span>
                  <span className="dot-sep">·</span>
                  <span>{timeAgo(m.createdAt)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form className="fb-thread-form" onSubmit={onSubmit}>
        <textarea
          className="fb-thread-input"
          placeholder={viewerRole === 'admin' ? '投稿者へ返信…' : '運営へ返信…'}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          rows={2}
          disabled={send.isPending}
        />
        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        <button
          type="submit"
          className="btn btn-primary sm"
          disabled={!body.trim() || send.isPending}
        >
          {send.isPending ? <Spinner /> : '送信'}
        </button>
      </form>
    </div>
  );
}
